import React, { useEffect, useRef } from 'react';
import {
  Animated,
  DimensionValue,
  Easing,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import { colors, radii, spacing } from '../../theme';

/**
 * The grey block a screen shows while its content is on the way.
 *
 * ---------------------------------------------------------------------------
 * WHY SKELETONS RATHER THAN A SPINNER
 * ---------------------------------------------------------------------------
 * A spinner says "wait" and nothing else. A skeleton says "wait, and here is
 * the shape of what is coming" - the page does not jump when the data lands,
 * because the space was already the right size, and the eye has somewhere to
 * rest that is not a rotating circle.
 *
 * It also stops the worst pattern in a list-driven app: a blank screen for
 * 400ms that reads as "nothing here" right up until it suddenly isn't.
 *
 * Spinners are still correct for one thing - a button that is busy. There the
 * shape is already on screen and only its state changed, which is why `Button`
 * keeps its `ActivityIndicator`.
 *
 * ---------------------------------------------------------------------------
 * ONE ANIMATION FOR EVERY BLOCK ON THE SCREEN
 * ---------------------------------------------------------------------------
 * A loading list has thirty of these on it. Thirty independent loops means
 * thirty animations to schedule, and - worse - thirty blocks pulsing out of
 * phase, which reads as static rather than as a page loading.
 *
 * So the pulse is a single module-level `Animated.Value` that every block
 * subscribes to, ref-counted: the loop starts when the first block mounts and
 * stops when the last one leaves, so nothing is animating while the app sits
 * on a screen that has finished loading.
 */

/** How far the fill fades at the bottom of the breath. */
const DIM = 0.4;
const FULL = 1;
/** One direction of the breath. 700 up, 700 down - a slow, calm pulse. */
const BEAT_MS = 700;

const pulse = new Animated.Value(0);

let subscribers = 0;
let loop: Animated.CompositeAnimation | null = null;

const beat = (toValue: number) =>
  Animated.timing(pulse, {
    toValue,
    duration: BEAT_MS,
    easing: Easing.inOut(Easing.quad),
    // Opacity is one of the few properties the native driver takes, which is
    // the whole reason the pulse is an opacity and not a background colour.
    useNativeDriver: true,
  });

const join = () => {
  if (subscribers++ > 0) {
    return;
  }
  loop = Animated.loop(Animated.sequence([beat(1), beat(0)]));
  loop.start();
};

const leave = () => {
  if (--subscribers > 0) {
    return;
  }
  loop?.stop();
  loop = null;
  // Reset, or the next screen's first frame inherits whatever opacity this
  // one happened to stop on.
  pulse.setValue(0);
};

const fade = (value: Animated.Value) =>
  value.interpolate({ inputRange: [0, 1], outputRange: [DIM, FULL] });

const opacity = fade(pulse);

/**
 * Subscribe a whole page of blocks at once.
 *
 * `TabSkeleton` draws forty blocks and holds the beat for all of them here,
 * passing it down as `pulse`. That is the cheaper of the two call styles when
 * you already have a parent that owns the whole skeleton - one subscription
 * instead of forty - and the value it returns is the same shared one either
 * way, so the two styles stay in phase with each other on a page that mixes
 * them.
 */
export function useShimmer(): Animated.Value {
  useSubscription();
  return pulse;
}

/** Ref-counted join/leave, shared by the hook and by a self-driving block. */
function useSubscription(active = true) {
  const joined = useRef(false);

  useEffect(() => {
    if (!active || joined.current) {
      return;
    }
    joined.current = true;
    join();
    return () => {
      joined.current = false;
      leave();
    };
  }, [active]);
}

type Props = {
  /** Defaults to filling its parent. */
  width?: DimensionValue;
  height?: number;
  /** Corner radius. Ignored when `circle` is set. */
  radius?: number;
  /** Square it off and round it fully - avatars, icon discs. `height` is the diameter. */
  circle?: boolean;
  /** Older spelling of `circle`, kept because `TabSkeleton` uses it. */
  round?: boolean;
  /**
   * A beat owned by a parent, from `useShimmer`.
   *
   * Omit it and the block drives itself, which is what a lone block inside a
   * real card wants. Pass it and the parent holds the subscription - see the
   * note on `useShimmer`.
   */
  pulse?: Animated.Value;
  style?: StyleProp<ViewStyle>;
};

function Skeleton({
  width = '100%',
  height = 12,
  radius,
  circle,
  round,
  pulse: given,
  style,
}: Props) {
  // Only when nobody above is already holding the beat.
  useSubscription(!given);

  const disc = circle || round;
  const tint = given ? fade(given) : opacity;

  return (
    <Animated.View
      // A placeholder has nothing to read out. Screen readers are told the
      // screen is loading once, by whatever is rendering these - not thirty
      // times by the blocks themselves.
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
      style={[
        styles.block,
        {
          width: disc ? height : width,
          height,
          borderRadius: disc ? height / 2 : radius ?? radii.md,
          opacity: tint,
        },
        style,
      ]}
    />
  );
}

/**
 * A paragraph's worth of blocks.
 *
 * The last line is short on purpose. Every line the same length reads as a
 * table; a ragged last line is what text actually looks like, and it is the
 * cheapest thing that makes a skeleton stop looking like a bar chart.
 */
export function SkeletonText({
  lines = 2,
  height = 11,
  gap = spacing.sm,
  lastWidth = '62%',
  style,
}: {
  lines?: number;
  height?: number;
  gap?: number;
  lastWidth?: DimensionValue;
  style?: StyleProp<ViewStyle>;
}) {
  return (
    <View style={[{ gap }, style]}>
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton
          key={i}
          height={height}
          radius={radii.sm}
          width={i === lines - 1 && lines > 1 ? lastWidth : '100%'}
        />
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  /*
   * The pale wash rather than a grey.
   *
   * `src/theme/colors.ts` has no neutral greys and says the six greens are the
   * only ones in the app. `surfaceMuted` is the step the app already uses for
   * "a surface with nothing on it yet", which is exactly what this is.
   */
  block: {
    backgroundColor: colors.surfaceMuted,
  },
});

export default Skeleton;
